/**
 * Cloud implementation of the shared SearchApi. Node-scoped, like the attendance / groups /
 * models / heatmap adapters: it binds the currently-selected node id.
 *
 * The route is Cloud FE → Cloud BE → tunnel → local BACKEND → command-center. That middle hop
 * through the node's backend rather than straight to its command-center is deliberate and is
 * what makes cloud search obey per-camera RBAC: the backend is the only service holding the
 * node's grants, and the tunnel attaches X-Cloud-User-Id for it to resolve the acting user. A
 * grant revoked on the node therefore takes effect on this dashboard immediately, with no
 * cloud-side copy of the camera list to go stale.
 *
 * With no node selected every call returns empty rather than throwing — the shared page renders
 * the host's empty state, and a dashboard that has not picked a node yet should not look broken.
 */
import type {
  SearchApi,
  SearchDsl,
  SearchFacets,
  SearchRefine,
  SearchResult,
  SearchSchema,
  CameraRef,
  Group,
  PersonRef,
} from '@trinetra/shared';
import {
  searchNodeEvents,
  searchNodeFacets,
  searchNodeRefine,
  getNodeSearchSchema,
  getNodeCameras,
  getNodeGroupsForFilter,
  getNodePersons,
} from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

/** Node id or '' — the shared page renders an empty state when no node is picked. */
const nid = () => nodeId() || '';

const EMPTY_RESULT: SearchResult = { success: true, events: 0, distinct_persons: 0, rows: [], camera_scope: 0 };

function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  return res?.data ?? res?.cameras ?? res?.groups ?? [];
}

export const cloudSearchApi: SearchApi = {
  async events(dsl: SearchDsl): Promise<SearchResult> {
    const id = nid();
    if (!id) return EMPTY_RESULT;
    // Not caught: the cloud api.service interceptor already reduces an error response to its
    // `message`, and that message is the server naming the bad filter — which the page shows
    // verbatim. Swallowing it here would leave the operator with an empty grid and no reason.
    return searchNodeEvents(id, dsl);
  },

  async facets(dsl: SearchDsl): Promise<SearchFacets> {
    const id = nid();
    if (!id) return {};
    const res = await searchNodeFacets(id, dsl);
    return res?.facets || {};
  },

  async refine(dsl: SearchDsl): Promise<SearchRefine> {
    const id = nid();
    if (!id) return { types: [], cameras: [], watchlist: [], attributes: {} };
    return searchNodeRefine(id, dsl);
  },

  async schema(): Promise<SearchSchema> {
    const id = nid();
    if (!id) return { attributes: {}, models: [], presets: [], aggregates: [] };
    const res = await getNodeSearchSchema(id);
    return res?.data || res;
  },

  /**
   * The HLS playlist for a camera over a window, node-scoped and tunnelled.
   *
   * Two differences from the local node, both forced by the cloud's shape:
   *   • the path carries the node id, because the cloud has many nodes;
   *   • the auth token goes in the QUERY STRING, because hls.js fetches segments with plain
   *     `fetch`/XHR and cannot attach an Authorization header to them.
   *
   * Mirrors exactly what the cloud Recordings page builds, so both play the same stream.
   */
  playbackUrl(cameraId: string, fromIso: string, toIso: string): string | null {
    const id = nid();
    const token = localStorage.getItem('cloud_token');
    if (!id || !cameraId || !token) return null;
    const qs = new URLSearchParams({ start_time: fromIso, end_time: toIso, token }).toString();
    return `${window.location.origin}/api/nodes/${id}/recordings/playlist/${cameraId}?${qs}`;
  },

  /**
   * Belt and braces on the cloud: the playlist URL already carries `?token=` (segment fetches
   * cannot set headers at all), but the cloud backend accepts the header too, and sending both
   * means a proxy route that only checks one still works.
   */
  playbackAuthToken(): string | null {
    return localStorage.getItem('cloud_token');
  },

  async listCameras(): Promise<CameraRef[]> {
    const id = nid();
    if (!id) return [];
    return toArray(await getNodeCameras(id)) as CameraRef[];
  },

  /**
   * `groups/for-filter` rather than the full `groups` list: it is scoped to the cameras this
   * user can actually see and needs no `groups.view`. A chip menu is labelling and filtering,
   * not group management, so it must not demand the permission that manages groups.
   */
  async listGroups(): Promise<Group[]> {
    const id = nid();
    if (!id) return [];
    return toArray(await getNodeGroupsForFilter(id)) as Group[];
  },

  /**
   * The selected node's registry, for recognising a name in a typed sentence.
   *
   * Needs `ai_events.view`, which a search user may not hold — the shared page treats a failure
   * here as "no names to match against" and leaves the rest of the parse working, so this must
   * be allowed to reject rather than being swallowed into an empty list that looks like an empty
   * registry.
   */
  async listPeople(): Promise<PersonRef[]> {
    const id = nid();
    if (!id) return [];
    const rows = toArray(await getNodePersons(id, { limit: '500' }));
    return rows.map((r: any) => ({ person_id: r.person_id, name: r.name })) as PersonRef[];
  },
};
